// What the builder draws on the grid besides the puzzle (mockup 07): the box where the logic
// stalls (shaded), true candidates (small grey digits in fixed places), suggestions (the clue
// faint, a dashed accent outline, a lettered badge), the line being drawn, the selected piece.
import type { ConstraintEntry, PuzzleDoc, Rc } from '@zinc/shared';
import { memo } from 'react';
import type { Geometry } from '../../lib/geometry';
import { cellsOf, clueOf, isEdge } from '../../lib/builder/model';
import { ConstraintsOver, ConstraintsUnder } from '../solve/constraints';
import { CELL } from '../solve/constraints/geom';

/** Mockup 07's values. */
const B = {
  stall: '#FFF6D6',
  candidate: '#8E8A82',
  accent: '#2F5FD0',
  drawing: '#A9C1F2',
  white: '#FFFFFF',
};

export const StallShade = memo(function StallShade({ cells }: { cells: Rc[] }) {
  return (
    <g pointerEvents="none" data-layer="stall">
      {cells.map(([r, c]) => (
        <rect
          key={`${r}-${c}`}
          x={c * CELL}
          y={r * CELL}
          width={CELL}
          height={CELL}
          fill={B.stall}
        />
      ))}
    </g>
  );
});

/** Digits still possible in each open cell, in fixed places (3×3, or 4×4 for 16 digits). */
export const Candidates = memo(function Candidates({
  geometry,
  candidates,
  hide,
}: {
  geometry: Geometry;
  candidates: string[][];
  /** Cells with a given or a single candidate: nothing small drawn. */
  hide: (i: number) => boolean;
}) {
  const n = geometry.digits.length;
  const side = n > 9 ? 4 : 3;
  const step = side === 4 ? 9 : 12;
  const first = side === 4 ? 6.5 : 8;
  const size = side === 4 ? 6.5 : 8;
  return (
    <g
      pointerEvents="none"
      data-layer="true-candidates"
      fontFamily="Inter, sans-serif"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={size}
      fontWeight={500}
      fill={B.candidate}
    >
      {geometry.cells.map(([r, c], i) => {
        const list = candidates[i];
        if (!list || list.length <= 1 || list.length === n || hide(i)) return null;
        return list.map((d) => {
          const k = geometry.digits.indexOf(d);
          if (k < 0) return null;
          return (
            <text
              key={`${i}-${d}`}
              x={c * CELL + first + step * (k % side)}
              y={r * CELL + first + step * Math.floor(k / side)}
            >
              {d}
            </text>
          );
        });
      })}
    </g>
  );
});

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  round: number;
  circle: boolean;
}

/** The outline around a piece: a circle on an edge clue, a box around one cell or a clue, a
 * pill around a line or an area. */
function outlineOf(cells: Rc[], edge: boolean): Box {
  if (edge && cells.length === 2) {
    const [a, b] = cells as [Rc, Rc];
    return {
      x: ((a[1] + b[1] + 1) / 2) * CELL - 12,
      y: ((a[0] + b[0] + 1) / 2) * CELL - 12,
      w: 24,
      h: 24,
      round: 12,
      circle: true,
    };
  }
  const rows = cells.map((c) => c[0]);
  const cols = cells.map((c) => c[1]);
  const r0 = Math.min(...rows);
  const r1 = Math.max(...rows) + 1;
  const c0 = Math.min(...cols);
  const c1 = Math.max(...cols) + 1;
  const inset = cells.length === 1 ? 4 : 6;
  const w = (c1 - c0) * CELL - 2 * inset;
  const h = (r1 - r0) * CELL - 2 * inset;
  return {
    x: c0 * CELL + inset,
    y: r0 * CELL + inset,
    w,
    h,
    round: cells.length === 1 ? 4 : Math.min(14, w / 2, h / 2),
    circle: false,
  };
}

function Outline({ box }: { box: Box }) {
  return box.circle ? (
    <circle
      cx={box.x + box.w / 2}
      cy={box.y + box.h / 2}
      r={box.w / 2}
      fill="none"
      stroke={B.accent}
      strokeWidth={1.5}
      strokeDasharray="3 2.5"
    />
  ) : (
    <rect
      x={box.x}
      y={box.y}
      width={box.w}
      height={box.h}
      rx={box.round}
      fill="none"
      stroke={B.accent}
      strokeWidth={1.5}
      strokeDasharray="3 2.5"
    />
  );
}

function pieceCells(entry: ConstraintEntry | null, cells: Rc[]): { cells: Rc[]; edge: boolean } {
  if (!entry) return { cells, edge: false };
  const clue = clueOf(entry);
  const drawn = cellsOf(entry);
  return { cells: drawn.length ? drawn : clue ? [clue] : cells, edge: isEdge(entry) };
}

export interface Marked {
  entry: ConstraintEntry | null;
  given: { cell: Rc; value: string } | null;
  cells: Rc[];
  /** "A", "B", "C" for suggestions; none for the selected piece. */
  letter?: string;
}

/** Suggestions (faint, outlined, lettered) and the selected piece (outlined). */
export function MarkedPieces({
  doc,
  geometry,
  marks,
}: {
  doc: PuzzleDoc;
  geometry: Geometry;
  marks: Marked[];
}) {
  return (
    <g pointerEvents="none" data-layer="suggestions">
      {marks.map((m, i) => {
        const { cells, edge } = pieceCells(m.entry, m.cells);
        if (cells.length === 0) return null;
        const box = outlineOf(cells, edge);
        const faintDoc: PuzzleDoc | null =
          m.letter && m.entry ? { ...doc, constraints: [m.entry] } : null;
        return (
          <g key={i}>
            {faintDoc && (
              <g opacity={0.6}>
                <ConstraintsUnder doc={faintDoc} geometry={geometry} />
                <ConstraintsOver doc={faintDoc} />
              </g>
            )}
            {m.letter && m.given && (
              <text
                x={(m.given.cell[1] + 0.5) * CELL}
                y={(m.given.cell[0] + 0.5) * CELL}
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily="Inter, sans-serif"
                fontSize={22}
                fontWeight={600}
                fill={B.accent}
                fillOpacity={0.55}
              >
                {m.given.value}
              </text>
            )}
            <Outline box={box} />
            {m.letter && (
              <g
                fontFamily="Inter, sans-serif"
                fontSize={10}
                fontWeight={700}
                textAnchor="middle"
                dominantBaseline="central"
              >
                <circle cx={box.x + box.w} cy={box.y} r={8} fill={B.accent} />
                <text x={box.x + box.w} y={box.y} fill={B.white}>
                  {m.letter}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
}

/** A line being drawn: pale accent, with its corners marked and its end dot (mockup 07). */
export function DrawingPath({ path, arrow }: { path: Rc[]; arrow: boolean }) {
  if (path.length === 0) return null;
  const pts = path.map(([r, c]) => [(c + 0.5) * CELL, (r + 0.5) * CELL] as const);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join('');
  const [sx, sy] = pts[0]!;
  const [ex, ey] = pts.at(-1)!;
  return (
    <g pointerEvents="none" data-layer="drawing">
      <path
        d={d}
        fill="none"
        stroke={B.drawing}
        strokeWidth={14}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {arrow ? (
        <circle cx={sx} cy={sy} r={16} fill={B.white} stroke={B.drawing} strokeWidth={3} />
      ) : (
        <circle cx={sx} cy={sy} r={15} fill={B.drawing} />
      )}
      {pts.slice(1, -1).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={4} fill={B.white} stroke={B.accent} strokeWidth={2} />
      ))}
      {pts.length > 1 && <circle cx={ex} cy={ey} r={5} fill={B.accent} />}
    </g>
  );
}

/** The region being painted: its cells outlined. */
export function RegionCells({ cells }: { cells: Rc[] }) {
  return (
    <g pointerEvents="none" data-layer="region">
      {cells.map(([r, c]) => (
        <rect
          key={`${r}-${c}`}
          x={c * CELL + 4}
          y={r * CELL + 4}
          width={CELL - 8}
          height={CELL - 8}
          rx={4}
          fill="none"
          stroke={B.accent}
          strokeWidth={1.5}
          strokeDasharray="3 2.5"
        />
      ))}
    </g>
  );
}
